import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'perf_detailed_v2';
const CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const readCache = (asOf) => {
  try {
    const p = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (!p?.ts || !p?.data || Date.now() - p.ts > CACHE_MAX_AGE_MS) return null;
    return (asOf || null) === (p.asOf || null) ? p.data : null;
  } catch { return null; }
};

/** PERF-STD-001: /performance/detailed with business-date selection. Stale-while-revalidate, in-flight guard, 30 s timeout. */
export const usePerformanceMetrics = (asOf) => {
  const [metrics, setMetrics] = useState(() => readCache(asOf));
  const [loading, setLoading] = useState(!metrics);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);
  const hasData = useRef(!!metrics);

  const fetchMetrics = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const first = !hasData.current;
    if (first) setLoading(true); else setSwitching(true);
    try {
      const params = { include_team: false };
      if (asOf) params.as_of = asOf;
      const res = await axios.get(`${API}/performance/detailed`, { params, timeout: 30000 });
      setMetrics(res.data);
      hasData.current = true;
      setError(null);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), asOf: asOf || null, data: res.data })); } catch { /* private mode */ }
    } catch (err) {
      setError(err?.code === 'ECONNABORTED' ? 'Performance data took too long to load. Please try again.' : 'Unable to load performance data');
      if (first) setMetrics({ can_view: false });
    } finally {
      inFlight.current = false;
      setLoading(false);
      setSwitching(false);
    }
  }, [asOf]);

  useEffect(() => {
    fetchMetrics();
    if (asOf) return undefined;
    const id = setInterval(fetchMetrics, 120000);
    return () => clearInterval(id);
  }, [fetchMetrics, asOf]);

  return { metrics, loading, switching, error, refetch: fetchMetrics };
};
